import { ModalDialog, type IconName } from '@amelio/ui-library';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import { TemplateCard, type TemplateCardProps } from '@/components/TemplateCard/TemplateCard';
import { TemplateCardSkeleton } from '@/components/TemplateCard/TemplateCardSkeleton';
import { useAmelioTranslations } from '@/hooks';

const SKELETON_CARD_COUNT = 3;

/** What a tile of the list shows of its template. */
export type TemplateListTile = Pick<
    TemplateCardProps,
    'title' | 'description' | 'questionCount' | 'countLabel'
>;

type TemplateListModalProps<TTemplate extends { id: string }> = {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle: string;
    iconName: IconName;
    templates: TTemplate[];
    toTile: (template: TTemplate) => TemplateListTile;
    isLoading: boolean;
    /** Replaces the tiles once loaded, when there are none to show or they could not be loaded. */
    notice?: string;
    /** Called when a template tile is activated: the tile and its preview button both open the
     * template preview, which is the only place a template can be imported from. */
    onPreview: (template: TTemplate) => void;
    dataTestId: string;
};

/** The Amelio catalogue of one kind of template, each read in its preview before it is imported. */
export const TemplateListModal = <TTemplate extends { id: string }>({
    isOpen,
    onClose,
    title,
    subtitle,
    iconName,
    templates,
    toTile,
    isLoading,
    notice,
    onPreview,
    dataTestId,
}: TemplateListModalProps<TTemplate>) => {
    const { t } = useAmelioTranslations();

    const tiles = () => {
        if (isLoading) {
            return Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
                <TemplateCardSkeleton key={index} />
            ));
        }

        return templates.map((template) => (
            // Without `onButtonClick`, the card's preview button lets its click
            // bubble to the card: mouse and keyboard reach the same handler.
            <TemplateCard
                key={template.id}
                {...toTile(template)}
                iconName={iconName}
                className={'p-6'}
                buttonText={t('templateManagement.template.importTemplatemodal.previewButton')}
                onCardClick={() => onPreview(template)}
            />
        ));
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={title}
            subtitle={subtitle}
            headingLevel={4}
            size={'lg'}
            backdropBlur
            // The tiles are white: on the tinted panel they stand apart, on a white one they vanish.
            className={'bg-gradient-modal-panel'}
            dataTestId={dataTestId}
        >
            {notice && !isLoading ? (
                <EmptyState message={notice} />
            ) : (
                <div className={'grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3'}>
                    {tiles()}
                </div>
            )}
        </ModalDialog>
    );
};
